/**
 * XtsArcCurve3 — Three.js Curve3 wrapper around the analytic Curve sample.
 *
 * Each module exposes a THREE.Curve<Vector3> that the part-level CurvePath
 * chains together. For analytic Curves we just wrap the math from
 * pathMath.ts; for Free paths see XtsPointCloudCurve3.
 *
 * Note: CurvePath delegates to subcurve length-proportional, so `getLength()`
 * MUST return the actual arc length so subcurves are weighted correctly.
 * Our subcurves are arc-length parametrised (t = s / L), so this just-works.
 */

import { Curve, Vector3 } from 'three';
import type { ModuleCatalogEntry } from './ModuleCatalog.js';
import { sampleModulePath } from './pathMath.js';

export class XtsArcCurve3 extends Curve<Vector3> {
  public readonly module: ModuleCatalogEntry;

  constructor(module: ModuleCatalogEntry) {
    super();
    if (module.pathType !== 'Curve') {
      throw new Error(
        `XtsArcCurve3 requires pathType='Curve', got "${module.pathType}"`,
      );
    }
    this.module = module;
  }

  override getPoint(t: number, optionalTarget = new Vector3()): Vector3 {
    const s = t * this.module.moduleLengthMm;
    const sample = sampleModulePath(this.module, s);
    return optionalTarget.set(
      sample.positionMm[0],
      sample.positionMm[1],
      sample.positionMm[2],
    );
  }

  override getTangent(t: number, optionalTarget = new Vector3()): Vector3 {
    const s = t * this.module.moduleLengthMm;
    const sample = sampleModulePath(this.module, s);
    return optionalTarget
      .set(sample.tangent[0], sample.tangent[1], sample.tangent[2])
      .normalize();
  }

  override getLength(): number {
    return this.module.moduleLengthMm;
  }
}
